import React from 'react'

import {
    Card,
    CardContent,
    Typography,
    Button,
    Box,
    Grid,
} from '@material-ui/core'
import {
    ArrowBack as ArrowLeft,
    Pause as PauseIcon,
    PlayArrow as PlayIcon,
} from '@material-ui/icons'
import { makeStyles, Theme } from '@material-ui/core/styles'

import {
    CampaignData,
    SequenceItem,
    SequenceStep,
    DelayStep,
} from '../../../Outbound/Campaign/CreateCampaign/types'
import { CampaignStatus } from '../../../../models/Outreach/types'

const useStyles = makeStyles((theme: Theme) => ({
    cardTitle: {
        fontSize: '1.25rem',
        fontWeight: 600,
        color: '#1f2937',
    },
    cardDescription: {
        color: '#6b7280',
        fontSize: '0.875rem',
        marginTop: theme.spacing(0.5),
    },
    summaryCard: {
        border: 'none',
        backgroundColor: '#ffffff',
        boxShadow:
            '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    },
    summaryItem: {
        display: 'flex',
        justifyContent: 'space-between',
        fontSize: '0.875rem',
        marginBottom: theme.spacing(1),
        '&:last-child': {
            marginBottom: 0,
        },
    },
    navigationContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: theme.spacing(3),
        padding: theme.spacing(2),
        backgroundColor: '#f8fafc',
        borderRadius: '12px',
        border: '1px solid #e2e8f0',
    },
    gradientButton: {
        background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
        color: 'white',
        borderRadius: '8px',
        '&:hover': {
            boxShadow:
                '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
        },
    },
    note: {
        marginTop: theme.spacing(1.5),
        fontSize: '0.8rem',
        color: theme.palette.text.secondary,
    },
}))

interface SeasonCampaignReviewTabProps {
    campaignData: CampaignData
    sequence: SequenceItem[]
    status: CampaignStatus
    canActivate: boolean
    activationBlockers?: string[]
    onBack: () => void
    onActivate: () => void
    onPause: () => void
    isSaving?: boolean
}

const isMessageStep = (item: SequenceItem): item is SequenceStep =>
    item.type === 'email' || item.type === 'sms'

const isDelayStep = (item: SequenceItem): item is DelayStep =>
    item.type === 'delay'

export const SeasonCampaignReviewTab: React.FC<SeasonCampaignReviewTabProps> = ({
    campaignData,
    sequence,
    status,
    canActivate,
    activationBlockers = [],
    onBack,
    onActivate,
    onPause,
    isSaving = false,
}) => {
    const classes = useStyles()
    const messageSteps = sequence.filter(isMessageStep)
    const delaySteps = sequence.filter(isDelayStep)
    const totalDays = delaySteps.reduce((sum, delay) => sum + delay.days, 0)
    const isActive = status === CampaignStatus.ACTIVE
    const isPaused = status === CampaignStatus.PAUSED

    return (
        <Box style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Card className={classes.summaryCard}>
                        <Box style={{ padding: '24px 24px 16px 24px' }}>
                            <Typography className={classes.cardTitle}>
                                Campaign review
                            </Typography>
                            <Typography className={classes.cardDescription}>
                                Review setup before activating. Prospects
                                already enrolled will start receiving messages
                                once active.
                            </Typography>
                        </Box>
                        <CardContent style={{ padding: '0 24px 24px 24px' }}>
                            <Box
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 12,
                                }}
                            >
                                <Box className={classes.summaryItem}>
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                    >
                                        Campaign name
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        style={{ fontWeight: 500 }}
                                    >
                                        {campaignData.name}
                                    </Typography>
                                </Box>
                                <Box className={classes.summaryItem}>
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                    >
                                        Reply-to
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        style={{ fontWeight: 500 }}
                                    >
                                        {campaignData.reply_to_email}
                                    </Typography>
                                </Box>
                                <Box className={classes.summaryItem}>
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                    >
                                        Message steps
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        style={{ fontWeight: 500 }}
                                    >
                                        {messageSteps.length}
                                    </Typography>
                                </Box>
                                <Box className={classes.summaryItem}>
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                    >
                                        Estimated duration
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        style={{ fontWeight: 500 }}
                                    >
                                        {totalDays}{' '}
                                        {totalDays === 1 ? 'day' : 'days'}
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12}>
                    <Card className={classes.summaryCard}>
                        <Box style={{ padding: '24px 24px 16px 24px' }}>
                            <Typography className={classes.cardTitle}>
                                {isActive ? 'Campaign status' : 'Activation'}
                            </Typography>
                        </Box>
                        <CardContent style={{ padding: '0 24px 24px 24px' }}>
                            {isActive ? (
                                <Button
                                    fullWidth
                                    size="large"
                                    variant="outlined"
                                    startIcon={<PauseIcon />}
                                    onClick={onPause}
                                    disabled={isSaving}
                                >
                                    Pause campaign
                                </Button>
                            ) : (
                                <>
                                    <Button
                                        fullWidth
                                        size="large"
                                        variant="contained"
                                        color="primary"
                                        startIcon={<PlayIcon />}
                                        className={classes.gradientButton}
                                        onClick={onActivate}
                                        disabled={!canActivate || isSaving}
                                    >
                                        {isSaving
                                            ? 'Saving…'
                                            : isPaused
                                            ? 'Save & resume campaign'
                                            : 'Save & activate campaign'}
                                    </Button>
                                    {!canActivate && (
                                        <Box className={classes.note}>
                                            {activationBlockers.length > 0 ? (
                                                activationBlockers.map(
                                                    (blocker) => (
                                                        <Typography
                                                            key={blocker}
                                                            component="p"
                                                            className={
                                                                classes.note
                                                            }
                                                            style={{
                                                                marginTop: 0,
                                                            }}
                                                        >
                                                            {blocker}
                                                        </Typography>
                                                    ),
                                                )
                                            ) : (
                                                <Typography
                                                    className={classes.note}
                                                >
                                                    Complete setup before
                                                    activating.
                                                </Typography>
                                            )}
                                        </Box>
                                    )}
                                </>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <Box className={classes.navigationContainer}>
                <Button
                    variant="outlined"
                    onClick={onBack}
                    startIcon={<ArrowLeft />}
                >
                    Back to sequence
                </Button>
            </Box>
        </Box>
    )
}
